import type { ChangeEvent, DragEvent, KeyboardEvent, RefObject } from 'react'
import { ArrowRight, CheckCircle2, FileText, Upload } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { PDF_FILE_ACCEPT } from '@/lib/files/pdf'

import { talentPoolCopy } from '@/app/talent-pool/copy'
import type { UploadPickerTrigger } from './talent-pool-page-types'

type TalentPoolLandingUploadSectionProps = {
  file: File | null
  storedFileName: string | null
  isDragging: boolean
  submitError: string
  canGoToPersonal: boolean
  fileInputRef: RefObject<HTMLInputElement | null>
  onDragOver: (event: DragEvent<HTMLDivElement>) => void
  onDragLeave: () => void
  onDrop: (event: DragEvent<HTMLDivElement>) => void
  onOpenPicker: (trigger: UploadPickerTrigger) => void
  onUploadAreaKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void
  onFileInputChange: (event: ChangeEvent<HTMLInputElement>) => void
  onRemoveFile: () => Promise<void>
  onLandingNext: () => void
}

export function TalentPoolLandingUploadSection({
  file,
  storedFileName,
  isDragging,
  submitError,
  canGoToPersonal,
  fileInputRef,
  onDragOver,
  onDragLeave,
  onDrop,
  onOpenPicker,
  onUploadAreaKeyDown,
  onFileInputChange,
  onRemoveFile,
  onLandingNext,
}: TalentPoolLandingUploadSectionProps) {
  const copy = talentPoolCopy

  return (
    <section
      id="talent-pool-upload"
      className="font-sans text-text-heading"
    >
      <div className="mx-auto flex w-full flex-col items-center justify-center gap-8 px-4 pb-10 pt-6 sm:px-6 lg:pb-14 lg:pt-8 lg:px-12">
        <div className="flex w-full flex-col items-center gap-6 text-center max-w-[800px]">
          <div className="flex flex-col items-center gap-2 lg:gap-5">
            <span className="inline-flex items-center justify-center rounded-full border-[2px] border-surface-primary bg-white dark:bg-card px-2 text-center text-sm font-normal leading-5 text-text-body">
              {copy.brand.tag}
            </span>
            <h1 className="text-[24px] font-semibold leading-[32px] tracking-[-0.2px] text-text-heading sm:text-4xl sm:leading-[44px] lg:text-[60px] lg:leading-[72px] lg:tracking-[-1.2px]">
              {copy.hero.heading}{' '}
              <span className="inline-flex items-center bg-status-error-bg px-2">
                {copy.hero.highlight}
              </span>
            </h1>
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-sm leading-5 text-text-muted sm:text-base sm:leading-7 lg:text-xl lg:leading-[30px]">
              {copy.hero.description}
            </p>
          </div>

          <div className="hidden flex-col gap-3 lg:flex">
            <div className="flex flex-wrap items-center justify-center gap-2 bg-surface-inverse px-4 py-2 text-card rounded-lg">
              {copy.hero.benefits.map((benefit) => (
                <span
                  key={benefit}
                  className="inline-flex items-center gap-1.5 text-sm font-medium"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {benefit}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="flex w-full justify-center">
          <div className="w-full max-w-[600px] rounded-2xl border border-border-primary bg-white dark:bg-card p-4 shadow-lg sm:p-5">
            <div
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              className={`relative cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${
                isDragging
                  ? 'border-brand-primary bg-brand-primary/10'
                  : file || storedFileName
                    ? 'border-status-success bg-status-success-bg'
                    : 'border-border-secondary bg-white dark:bg-card hover:border-brand-primary hover:bg-brand-primary/5'
              }`}
              onClick={() => {
                onOpenPicker('mouse')
              }}
              role="button"
              tabIndex={0}
              onKeyDown={onUploadAreaKeyDown}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={PDF_FILE_ACCEPT}
                className="hidden"
                onChange={onFileInputChange}
              />
              {file || storedFileName ? (
                <div className="flex flex-col items-center gap-2">
                  <FileText className="h-10 w-10 text-status-success" />
                  <p className="text-sm font-semibold text-text-heading">
                    {file?.name ?? storedFileName}
                  </p>
                  <p className="text-xs text-text-muted">{copy.upload.ready}</p>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation()
                      void onRemoveFile()
                    }}
                    className="mt-1 text-xs font-semibold text-brand-primary hover:underline"
                  >
                    {copy.upload.changeFile}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-10 w-10 text-text-muted" />
                  <p className="text-sm font-semibold text-text-body">
                    {copy.upload.label}
                  </p>
                </div>
              )}
            </div>

            {submitError && (
              <p className="mt-3 text-center text-xs text-brand-primary">{submitError}</p>
            )}

            <Button
              type="button"
              disabled={!canGoToPersonal}
              onClick={onLandingNext}
              className="mt-4 h-12 w-full rounded-xl bg-brand-primary text-base font-semibold text-white shadow-button hover:bg-brand-primary-hover"
            >
              {copy.common.next}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>

            <p className="mt-3 text-center text-xs text-text-muted">
              {copy.upload.hint}
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
